import { useState } from 'react';
import { Configuration, OrdersApi } from '@/api';
import MenuButton from '@/components/MenuButton';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { Label, Radio, RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

enum ExportOption {
  AllOrders = 'ALL_ORDERS',
  FilteredOrders = 'FILTERED_ORDERS',
  SelectedOrders = 'SELECTED_ORDERS',
}

const exportOptions = [
  { label: 'All Orders', value: ExportOption.AllOrders },
  { label: 'Filtered Orders', value: ExportOption.FilteredOrders },
  { label: 'Selected Orders', value: ExportOption.SelectedOrders },
];

interface ExportOrdersButtonProps {
  searchQuery?: string | null;
  supplierId?: string | null;
  orderStatus?: string | null;
  orderIds?: string[] | null;
  offset?: number;
  limit?: number;
  format?: 'csv' | 'xlsx';
}

const ExportOrdersButton = ({
  searchQuery,
  supplierId,
  orderStatus,
  orderIds,
  offset,
  limit,
  format: initialFormat = 'csv',
}: ExportOrdersButtonProps) => {
  const ordersApi = new OrdersApi(new Configuration(useAuth));
  const [format, setFormat] = useState<'csv' | 'xlsx'>(initialFormat);

  const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (option: ExportOption) => {
    try {
      let exportQuery: string | null = null;
      let exportFilters: { supplierId?: string; status?: string } | undefined = undefined;
      let exportOrderIds: string[] | undefined = undefined;

      switch (option) {
        case ExportOption.AllOrders:
          exportFilters = {
            ...(supplierId ? { supplierId } : {}),
            ...(orderStatus ? { status: orderStatus } : {}),
          };
          break;
        case ExportOption.FilteredOrders:
          exportQuery = searchQuery || null;
          exportFilters = {
            ...(supplierId ? { supplierId } : {}),
            ...(orderStatus ? { status: orderStatus } : {}),
          };
          break;
        case ExportOption.SelectedOrders:
          if (!orderIds || orderIds.length === 0) {
            console.warn('No orders selected for export.');
            return;
          }
          exportOrderIds = orderIds;
          break;
        default:
          console.warn('Unsupported export option:', option);
          return;
      }

      const blob = await ordersApi.exportOrders(
        exportQuery,
        exportFilters,
        exportOrderIds,
        offset,
        limit,
        format,
      );

      const fileExtension = format === 'xlsx' ? 'xlsx' : 'csv';
      const filename = `orders-${option.toLowerCase?.() || option}-${Date.now()}.${fileExtension}`;

      downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to export orders:', error);
    }
  };

  const formatToggle = (
    <div className="flex items-center justify-center h-full">
      <RadioGroup value={format} onChange={setFormat} className="flex space-x-4">
        {[
          { label: 'CSV', value: 'csv' },
          { label: 'Excel', value: 'xlsx' },
        ].map(({ label, value }) => (
          <Radio
            key={value}
            value={value}
            className={({ focus, checked }) =>
              `relative flex items-center justify-between gap-1 cursor-pointer rounded-lg px-2 py-1.5 shadow-md focus:outline-none ${
                checked ? 'bg-blue-500 text-white' : 'bg-white text-gray-900'
              } ${focus ? 'ring-2 ring-blue-500 ring-opacity-60' : ''}`
            }
          >
            {({ checked }) => (
              <>
                <Label
                  as="span"
                  className={`block text-xs font-medium ${checked ? 'text-white' : 'text-gray-900 px-2'}`}
                >
                  {label}
                </Label>

                {checked && (
                  <div className="flex items-center justify-center w-4 h-4 bg-white rounded-full">
                    <CheckIcon className="w-3 h-3 text-blue-600" aria-hidden="true" />
                  </div>
                )}
              </>
            )}
          </Radio>
        ))}
      </RadioGroup>
    </div>
  );

  return (
    <MenuButton
      data-testid="order-list-export-button"
      label="Export"
      options={exportOptions}
      onClick={(option) => handleExport(option.value)}
      dropdownAfter={formatToggle}
    />
  );
};

export default ExportOrdersButton;
